/**
 * HTML overlay: title / dead / finished / paused screens + in-run HUD
 * (progress bar, time, bits, debug readout). Pure DOM, no per-frame allocation
 * beyond the strings we must set.
 */

export type ScreenName =
  | "title"
  | "dead"
  | "results"
  | "paused"
  | "settings"
  | "calibration"
  | "touchhint"
  | "levelselect"
  | "prerun"
  | "practice"
  | "daily"
  | "ships"
  | null;

function el<T extends HTMLElement>(id: string): T {
  const node = document.getElementById(id);
  if (!node) throw new Error(`missing #${id}`);
  return node as T;
}

/** Gravity indicator copy (SkyRoads update): shared by HUD chip, pre-run
 *  and daily screens so the presets always read the same everywhere. */
export function gravityLabel(preset: "floaty" | "normal" | "heavy", long = false): string {
  switch (preset) {
    case "floaty":
      return long ? "↟ FLOATY GRAVITY — LONG ARCS" : "↟ FLOATY";
    case "heavy":
      return long ? "↡ HEAVY GRAVITY — SHORT HOPS" : "↡ HEAVY";
    default:
      return long ? "NORMAL GRAVITY" : "NORMAL";
  }
}

export function applyGravityClass(node: HTMLElement, preset: "floaty" | "normal" | "heavy"): void {
  node.classList.remove("g-floaty", "g-normal", "g-heavy");
  node.classList.add(`g-${preset}`);
}

export function formatTimeMs(ms: number): string {
  const totalMs = Math.max(0, Math.round(ms));
  const m = Math.floor(totalMs / 60000);
  const s = Math.floor((totalMs % 60000) / 1000);
  const frac = totalMs % 1000;
  return `${m}:${s.toString().padStart(2, "0")}.${frac.toString().padStart(3, "0")}`;
}

export class Hud {
  private screens: Record<Exclude<ScreenName, null>, HTMLElement> = {
    title: el("screen-title"),
    dead: el("screen-dead"),
    results: el("screen-results"),
    paused: el("screen-paused"),
    settings: el("screen-settings"),
    calibration: el("screen-calibration"),
    touchhint: el("screen-touch-hint"),
    levelselect: el("screen-levelselect"),
    prerun: el("screen-prerun"),
    practice: el("screen-practice"),
    daily: el("screen-daily"),
    ships: el("screen-ships"),
  };
  private titlePrompt = el("title-prompt");
  private countIn = el("count-in");
  private tutPrompt = el("tut-prompt");
  private deadHint = el("dead-hint");
  private hud = el("hud");
  private progressFill = el("progress-fill");
  private hudTime = el("hud-time");
  private hudBits = el("hud-bits");
  private debug = el("debug");
  private deadProgress = el("dead-progress");

  showScreen(name: ScreenName): void {
    for (const [key, screen] of Object.entries(this.screens)) {
      screen.classList.toggle("hidden", key !== name);
    }
  }

  setHudVisible(visible: boolean): void {
    this.hud.classList.toggle("visible", visible);
  }

  setDeathInfo(progressPct: number, respawnAvailable: boolean): void {
    this.deadProgress.textContent = `${Math.floor(progressPct)}% — so close.`;
    this.deadHint.textContent = respawnAvailable
      ? "R / tap — from checkpoint"
      : "R / tap — retry";
  }

  updateRun(progressPct: number, timeMs: number, bits: number, bitsTotal: number): void {
    this.progressFill.style.width = `${progressPct.toFixed(1)}%`;
    this.hudTime.textContent = formatTimeMs(timeMs);
    this.hudBits.textContent = `◆ ${bits}/${bitsTotal}`;
  }

  // --- R2-M4 juice -----------------------------------------------------------
  private speedlines = el("speedlines");
  private ripple = el("ripple");
  private lastSpeedlineIntensity = -1;

  /** Build the radial streak set once (20 divs, CSS-animated). */
  initSpeedLines(): void {
    if (this.speedlines.childElementCount > 0) return;
    for (let i = 0; i < 20; i++) {
      const d = document.createElement("div");
      d.className = "sl";
      // ring of directions, biased away from screen center bottom (the road)
      const angle = (i / 20) * 360;
      d.style.setProperty("--a", `${angle}deg`);
      d.style.animationDelay = `${(i * 0.37) % 0.42}s`;
      this.speedlines.appendChild(d);
    }
  }

  /** Speed-lines opacity 0..1 (VFX-1). Caller gates by quality + motion. */
  setSpeedLines(intensity: number): void {
    const v = Math.max(0, Math.min(1, intensity));
    if (v === this.lastSpeedlineIntensity) return;
    this.lastSpeedlineIntensity = v;
    this.speedlines.style.opacity = v.toFixed(2);
  }

  /** 250 ms death desaturation ripple (VFX-7) — a single sub-3 Hz event. */
  flashRipple(): void {
    this.ripple.classList.remove("go");
    // force a reflow so re-adding the class restarts the animation
    void this.ripple.offsetWidth;
    this.ripple.classList.add("go");
  }

  /** Bit collect (VFX-4): a glint flies from the pickup to the counter. */
  flyGlint(fromXpx: number, fromYpx: number): void {
    const dot = document.createElement("div");
    dot.className = "glint";
    dot.style.left = `${fromXpx}px`;
    dot.style.top = `${fromYpx}px`;
    document.body.appendChild(dot);
    const target = this.hudBits.getBoundingClientRect();
    requestAnimationFrame(() => {
      dot.style.transform = `translate(${target.left + 12 - fromXpx}px, ${target.top + 8 - fromYpx}px) scale(0.4)`;
      dot.style.opacity = "0";
    });
    setTimeout(() => {
      dot.remove();
      this.hudBits.classList.remove("pop");
      void this.hudBits.offsetWidth;
      this.hudBits.classList.add("pop");
    }, 330);
  }

  /** Count-in display: pass beats remaining (> 0) or null to hide. */
  setCountIn(beats: number | null): void {
    this.countIn.textContent = beats === null ? "" : String(beats);
  }

  private gravityChip = el("gravity-chip");

  /** In-run gravity chip — set once per run start. */
  setGravity(preset: "floaty" | "normal" | "heavy"): void {
    this.gravityChip.textContent = gravityLabel(preset);
    applyGravityClass(this.gravityChip, preset);
  }

  setTutorialPrompt(text: string | null): void {
    this.tutPrompt.textContent = text ?? "";
  }

  private onBeatEl = el("on-beat");
  private onBeatTimer: ReturnType<typeof setTimeout> | null = null;
  private throttleFill = el("throttle-fill");
  private throttleChevron = el("throttle-chevron");

  /**
   * Throttle gauge (R2 §1.1): chevron at current value, fill from the 1.0
   * detent; warms toward the accent above 1.1, cools below 0.9. No numbers.
   */
  setThrottle(throttle: number, effRate: number): void {
    // map [0.6, 1.4] → [bottom 100%, top 0%]
    const frac = (throttle - 0.6) / 0.8;
    const pct = (1 - frac) * 100;
    this.throttleChevron.style.top = `${pct}%`;
    const top = Math.min(pct, 50);
    this.throttleFill.style.top = `${top}%`;
    this.throttleFill.style.height = `${Math.abs(pct - 50)}%`;
    const color =
      effRate > 1.1 ? "var(--accent)" : effRate < 0.9 ? "#4A6C8C" : "var(--neon)";
    this.throttleFill.style.background = color;
    this.throttleChevron.style.color = color;
  }

  // --- touch v3 joystick indicator (D-055) -----------------------------------
  private joyRing = el("joy-ring");
  private joyDot = el("joy-dot");
  private joyVisible = false;

  /** Show the anchor ring + deflection dot while a steer thumb is down. */
  setJoystick(state: { anchorX: number; anchorY: number; dx: number } | null): void {
    const visible = state !== null;
    if (visible !== this.joyVisible) {
      this.joyVisible = visible;
      this.joyRing.style.display = visible ? "block" : "none";
      this.joyDot.style.display = visible ? "block" : "none";
    }
    if (state) {
      this.joyRing.style.left = `${state.anchorX}px`;
      this.joyRing.style.top = `${state.anchorY}px`;
      this.joyDot.style.left = `${state.anchorX + state.dx * 36}px`;
      this.joyDot.style.top = `${state.anchorY}px`;
    }
  }

  /** "ON BEAT!" flash (spec 5.3) — capped well under 3 Hz by its own timer. */
  flashOnBeat(): void {
    if (this.onBeatTimer !== null) return; // still showing — no strobe
    this.onBeatEl.classList.add("flash");
    this.onBeatTimer = setTimeout(() => {
      this.onBeatEl.classList.remove("flash");
      this.onBeatTimer = null;
    }, 450);
  }

  setDebug(text: string): void {
    this.debug.textContent = text;
  }

  setTitlePrompt(text: string): void {
    this.titlePrompt.textContent = text;
  }
}
